<template>
    <div class="animated fadeIn">
        <!-- REMOVED: Nested ValidationObserver was unnecessary
             - Had two ValidationObserver components: one wrapping the entire template and one inside the card
             - Only one ValidationObserver is needed for form validation
             - The inner ValidationObserver is sufficient and should be kept
             - The outer one was redundant and caused unnecessary re-renders -->
        <b-row>
            <b-col md="12">
                <component-header
                    title="Create Access Rules"
                    :hasSearch="false"
                    :hasReload="false"
                    :hasCreate="false"
                    :hasback="true"
                    @back-press="$router.push({ path: '/setup/accessrule' })"
                />
            </b-col>
            <b-col md="12">
                <b-card>
                    <!-- Loading overlay for permissions -->
                    <div v-if="isLoadingPermissions" class="loading-overlay">
                        <div class="loading-content">
                            <b-spinner
                                variant="primary"
                                label="Loading permissions..."
                            ></b-spinner>
                            <p class="mt-2">Loading permissions...</p>
                        </div>
                    </div>

                    <!-- KEPT: Single ValidationObserver for form validation -->
                    <ValidationObserver ref="observer" v-slot="{ invalid }">
                        <form @submit.prevent="handleSubmit">
                            <b-row class="mt-4">
                                <b-col md="6">
                                    <label for="planName">Name</label>
                                    <ValidationProvider
                                        rules="required"
                                        v-slot="{ errors }"
                                        name="planName"
                                    >
                                        <b-form-input
                                            id="planName"
                                            inputType="text"
                                            placeholder="Enter access rule name"
                                            v-model="inputObject.name"
                                            :disabled="
                                                loading || isLoadingPermissions
                                            "
                                            :class="{ 'is-invalid': errors[0] }"
                                        />
                                        <span class="validate-error">{{
                                            errors[0]
                                        }}</span>
                                    </ValidationProvider>
                                </b-col>
                                <b-col md="6">
                                    <label for="keys">Keys</label>
                                    <ValidationProvider
                                        rules="required"
                                        v-slot="{ errors }"
                                        name="keys"
                                    >
                                        <multiselect
                                            v-model="inputObject.keys"
                                            tag-placeholder="Add this as new keys"
                                            :placeholder="
                                                isLoadingPermissions
                                                    ? 'Loading permissions...'
                                                    : 'Select keys List'
                                            "
                                            label="name"
                                            track-by="keys"
                                            :options="permissions"
                                            :multiple="true"
                                            :taggable="false"
                                            :open-direction="'bottom'"
                                            :disabled="
                                                loading || isLoadingPermissions
                                            "
                                            :class="{ 'is-invalid': errors[0] }"
                                        ></multiselect>
                                        <span class="validate-error">{{
                                            errors[0]
                                        }}</span>
                                        <small
                                            v-if="isLoadingPermissions"
                                            class="text-muted"
                                        >
                                            Please wait while permissions are
                                            being loaded...
                                        </small>
                                    </ValidationProvider>
                                </b-col>
                            </b-row>
                            <div class="text-center">
                                <button
                                    type="submit"
                                    class="btn btn-primary mt-4"
                                    :disabled="
                                        invalid ||
                                        loading ||
                                        isLoadingPermissions
                                    "
                                >
                                    <span v-if="loading">
                                        <b-spinner
                                            small
                                            class="mr-2"
                                        ></b-spinner>
                                        Saving...
                                    </span>
                                    <span v-else>Save</span>
                                </button>
                            </div>
                        </form>
                    </ValidationObserver>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {
    ValidationProvider,
    ValidationObserver,
} from "vee-validate/dist/vee-validate.esm";
// import ebSelect from "@/components/FormElements/Select.vue";
import { listPermissions } from "@/services/auth";
// import ebButton from "@/components/FormElements/Button";
import ComponentHeader from "@/components/componentNewHeader";
export default {
    name: "createfaq",
    components: {
        ValidationProvider,
        ValidationObserver,
        ComponentHeader,
    },
    data() {
        return {
            loading: false,
            isLoadingPermissions: false,
            error: null,
            inputObject: {
                name: "",
                keys: [],
            },
            permissions: [],
        };
    },
    methods: {
        async handleSubmit() {
            // Reset error state
            this.error = null;

            // Validate form
            const isValid = await this.$refs.observer.validate();

            if (!isValid) {
                this.$swal({
                    title: "Validation Error",
                    text: "Please fill in all required fields correctly",
                    type: "warning",
                    confirmButtonText: "OK",
                });
                return;
            }

            // Validate keys array
            if (
                !Array.isArray(this.inputObject.keys) ||
                this.inputObject.keys.length === 0
            ) {
                this.$swal({
                    title: "Validation Error",
                    text: "Please select at least one key",
                    type: "warning",
                    confirmButtonText: "OK",
                });
                return;
            }

            // Validate name
            const trimmedName = this.inputObject.name?.trim();
            if (!trimmedName || trimmedName.length === 0) {
                this.$swal({
                    title: "Validation Error",
                    text: "Please enter a valid access rule name",
                    type: "warning",
                    confirmButtonText: "OK",
                });
                return;
            }

            this.loading = true;

            try {
                // Extract keys from selected objects
                const keysArray = this.inputObject.keys
                    .map((item) => {
                        if (typeof item === "string") {
                            return item;
                        }
                        return item?.keys || item?.name || item;
                    })
                    .filter(Boolean);

                if (keysArray.length === 0) {
                    throw new Error("No valid keys selected");
                }

                const payload = {
                    name: trimmedName,
                    keys: keysArray,
                };

                const result = await this.$store.dispatch(
                    "accessrule/createAccessrule",
                    payload,
                );

                // Check if API returned an error message
                if (result?.message && result.message !== "success") {
                    throw new Error(result.message);
                }

                this.$swal({
                    title: "Success",
                    text: "Access Rule created successfully!",
                    type: "success",
                    confirmButtonText: "OK",
                    timer: 2000,
                });

                // Navigate after a short delay to allow user to see success message
                setTimeout(() => {
                    this.$router.push({ path: "/setup/accessrule" });
                }, 500);
            } catch (error) {
                this.error = error;

                // Extract error message
                let errorMessage = "Failed to create access rule";

                if (error?.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error?.message) {
                    errorMessage = error.message;
                } else if (typeof error === "string") {
                    errorMessage = error;
                }

                // Show error notification
                this.$toasted.error(errorMessage);

                // Show error alert
                this.$swal({
                    title: "Error",
                    text: errorMessage,
                    type: "error",
                    confirmButtonText: "OK",
                });
            } finally {
                this.loading = false;
            }
        },
        async loadPermissions() {
            this.isLoadingPermissions = true;
            this.error = null;

            try {
                const result = await listPermissions();

                // Validate response structure
                if (!result || !result.response) {
                    throw new Error("Invalid response format from server");
                }

                if (!Array.isArray(result.response)) {
                    throw new Error(
                        "Permissions data is not in expected format",
                    );
                }

                // Transform permissions data
                this.permissions = result.response
                    .filter((permission) => permission && permission.name)
                    .map((permission) => ({
                        name: permission.name,
                        keys: permission.name,
                    }));

                if (this.permissions.length === 0) {
                    this.$toasted.warning("No permissions available");
                }
            } catch (error) {
                this.error = error;

                // Extract error message
                let errorMessage = "Failed to load permissions";

                if (error?.response?.status === 401) {
                    errorMessage = "Unauthorized. Please login again.";
                } else if (error?.response?.status === 403) {
                    errorMessage =
                        "You don't have permission to access this resource.";
                } else if (error?.response?.status >= 500) {
                    errorMessage = "Server error. Please try again later.";
                } else if (error?.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error?.message) {
                    errorMessage = error.message;
                }

                this.$toasted.error(errorMessage);

                // Show error alert for critical errors
                if (error?.response?.status >= 500 || !error?.response) {
                    this.$swal({
                        title: "Error",
                        text: errorMessage,
                        type: "error",
                        confirmButtonText: "OK",
                    });
                }
            } finally {
                this.isLoadingPermissions = false;
            }
        },
    },
    async mounted() {
        await this.loadPermissions();
    },
};
</script>
<style scoped>
.btn-save {
    min-width: 120px;
    min-height: 60px;
    padding: 0.5rem 1.5rem;
}

.validate-error {
    color: #dc3545;
    font-size: 0.875rem;
    display: block;
    margin-top: 0.25rem;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    border-radius: 0.25rem;
}

.loading-content {
    text-align: center;
}

.loading-content p {
    margin: 0;
    color: #6c757d;
    font-size: 0.875rem;
}

:deep(.card) {
    position: relative;
}

/* Disabled state styling */
.form-control:disabled,
.multiselect--disabled {
    background-color: #e9ecef;
    opacity: 0.6;
    cursor: not-allowed;
}

/* Button loading state */
.btn-primary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.btn-primary .spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.15em;
}

/* Error state styling */
.is-invalid {
    border-color: #dc3545;
}

.is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
</style>
